// Section 17 and 18 (rations), 24.14 actions: the Rations tab and the shortage dialog. Edits to
// the decision draft are allowed only while deciding; shortage answers only while paused (24.7).
// Every edit is a change relative to the committed state, so an unchanged value is dropped from
// the draft (Section 4: "only changes are logged").
import type {
  Decision,
  FoodFloor,
  GameState,
  HouseholdId,
  PersonId,
  Policies,
  PriorityGroupId,
  PromptAnswer,
  PromptId,
  RationOverrides,
  RationPolicyId,
  SavedPriorityOrder,
  SeedReserveOption,
  WarmthOption,
  WaterRationOption,
} from '../../sim/api';
import { withBoundary } from '../boundary';
import { draft, patchUi } from '../state';
import { answerPrompt, requireGame, requireStatus } from './game';

/** The resources the Rations tab edits (medicine follows the health-care policy, Section 12). */
export type RationedResource = 'food' | 'water' | 'warmth';

/** The option set of each resource's ration policy (Section 18). */
export interface RationPolicyOptions {
  food: RationPolicyId;
  water: WaterRationOption;
  warmth: WarmthOption;
}

const POLICY_KEY = {
  food: 'foodRation',
  water: 'waterRation',
  warmth: 'warmth',
} as const satisfies Record<RationedResource, keyof Policies>;

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function edit(change: (d: Decision, state: GameState) => Decision): void {
  requireStatus('deciding');
  const state = requireGame();
  const current = draft.value;
  if (current === null) return;
  draft.value = change(current, state);
  patchUi({ problems: [] });
}

/** Set a policy in the draft, or drop it when it equals the committed value. */
function withPolicy<K extends keyof Policies>(
  d: Decision,
  state: GameState,
  id: K,
  value: Policies[K],
): Decision {
  const policies = { ...d.policies };
  if (same(state.policies[id], value)) delete policies[id];
  else policies[id] = value;
  return { ...d, policies };
}

/** The value in force once the draft applies (draft first, then the committed state). */
export function effectivePolicy<K extends keyof Policies>(
  state: GameState,
  d: Decision | null,
  id: K,
): Policies[K] {
  return (d?.policies[id] as Policies[K] | undefined) ?? state.policies[id];
}

export function effectiveSavedOrders(state: GameState, d: Decision | null): SavedPriorityOrder[] {
  return d?.savedPriorityOrders ?? state.savedPriorityOrders;
}

export function effectiveOverrides(
  state: GameState,
  d: Decision | null,
  resource: RationedResource,
): RationOverrides {
  return d?.rations[resource] ?? state.rationOverrides[resource];
}

export const setRationPolicy = withBoundary(
  <R extends RationedResource>(resource: R, policyId: RationPolicyOptions[R]): void => {
    edit((d, s) => withPolicy(d, s, POLICY_KEY[resource], policyId as never));
  },
);

export const setFoodFloor = withBoundary((floor: FoodFloor): void => {
  edit((d, s) => withPolicy(d, s, 'foodFloor', floor));
});

/** Section 17 priority editor: the new ranked list of groups (drag or arrow keys). */
export const reorderPriority = withBoundary((groupIds: readonly PriorityGroupId[]): void => {
  edit((d, s) => withPolicy(d, s, 'foodPriority', [...new Set(groupIds)]));
});

export const setAnnounce = withBoundary((announced: boolean): void => {
  edit((d, s) => withPolicy(d, s, 'announceRations', announced));
});

export const setSeedReserve = withBoundary((option: SeedReserveOption): void => {
  edit((d, s) => withPolicy(d, s, 'seedReserve', option));
});

export const setAskOnShortage = withBoundary((on: boolean): void => {
  edit((d, s) => withPolicy(d, s, 'askOnShortage', on));
});

export const setHouseholdSharing = withBoundary((on: boolean): void => {
  edit((d, s) => withPolicy(d, s, 'householdSharing', on));
});

function withSavedOrders(d: Decision, s: GameState, orders: SavedPriorityOrder[]): Decision {
  return { ...d, savedPriorityOrders: same(orders, s.savedPriorityOrders) ? null : orders };
}

/** Save the current (drafted) priority order as a named preset ("Winter plan"). */
export const savePriorityOrder = withBoundary((name: string, announced: boolean): void => {
  const label = name.trim();
  if (label === '') return;
  edit((d, s) => {
    const orders = effectiveSavedOrders(s, d);
    let n = orders.length + 1;
    while (orders.some((o) => o.id === `order-${n}`)) n++;
    const order: SavedPriorityOrder = {
      id: `order-${n}`,
      name: label.slice(0, 40),
      groups: [...effectivePolicy(s, d, 'foodPriority')],
      announced,
    };
    return withSavedOrders(d, s, [...orders, order]);
  });
});

/** Swap in a saved order in one click: its groups and its announced flag. */
export const applySavedOrder = withBoundary((id: string): void => {
  edit((d, s) => {
    const order = effectiveSavedOrders(s, d).find((o) => o.id === id);
    if (order === undefined) return d;
    return withPolicy(
      withPolicy(d, s, 'foodPriority', [...order.groups]),
      s,
      'announceRations',
      order.announced,
    );
  });
});

export const deleteSavedOrder = withBoundary((id: string): void => {
  edit((d, s) =>
    withSavedOrders(
      d,
      s,
      effectiveSavedOrders(s, d).filter((o) => o.id !== id),
    ),
  );
});

/**
 * Pin a person or a household to a share of need (1 full, 0.5 half, 0 none), or clear the pin
 * with null. Section 17: pins are honoured before the policy fills the rest. Warmth pins are
 * per household and apply in Winter only.
 */
export const pinRation = withBoundary(
  (
    target: PersonId | HouseholdId,
    share: number | null,
    resource: RationedResource = 'food',
  ): void => {
    edit((d, s) => {
      const current = effectiveOverrides(s, d, resource);
      const next: RationOverrides = {
        people: { ...current.people },
        households: { ...current.households },
      };
      const clamped = share === null ? null : Math.min(1, Math.max(0, share));
      if (target.startsWith('h')) {
        const id = target as HouseholdId;
        if (clamped === null) delete next.households[id];
        else next.households[id] = clamped;
      } else {
        const id = target as PersonId;
        if (clamped === null) delete next.people[id];
        else next.people[id] = clamped;
      }
      const rations = { ...d.rations };
      if (same(next, s.rationOverrides[resource])) delete rations[resource];
      else rations[resource] = next;
      return { ...d, rations };
    });
  },
);

/** Section 17 "My household": the named list behind the `my-household` priority group. */
export const setMyHousehold = withBoundary((personIds: readonly PersonId[]): void => {
  edit((d, s) => {
    const list = [...new Set(personIds)].sort();
    return { ...d, myHousehold: same(list, s.myHousehold) ? null : list };
  });
});

/** Answer the shortage dialog and resume the season (allowed only while paused). */
export const answerShortage = withBoundary(
  (promptId: PromptId, answer: Extract<PromptAnswer, { kind: 'shortage' }>): void => {
    requireStatus('paused');
    answerPrompt(promptId, answer);
  },
);
